import { fetchApi, fetchRest, getSellerImageUrl } from '@/config/api'
import type {
  LookbookDisplay,
  CreateLookbookInput,
  UpdateLookbookInput,
  LookbookFiche,
  LookbookFicheProduit,
  LookbookCategorie,
  LookbookFormData,
} from '@/types/lookbook'

// ============= TRANSFORMATEURS =============

/**
 * Transforme un lookbook backend en lookbook pour l'affichage
 */
export function transformLookbookToDisplay(lookbook: any): LookbookDisplay {
  const data = lookbook.data ? JSON.parse(lookbook.data) : {}
  
  // Construire l'URL complète de la bannière (depuis le storage)
  const banniereUrl = lookbook.banniere_url ? getSellerImageUrl(lookbook.banniere_url) : undefined

  return {
    id: lookbook.id_mod_lookbook,
    titre: {
      francais: lookbook.titre_fr || '',
      anglais: lookbook.titre_en || '',
      espagnol: lookbook.titre_es || '',
      italien: lookbook.titre_it || '',
    },
    description: {
      francais: lookbook.description_fr || '',
      anglais: lookbook.description_en || '',
      espagnol: lookbook.description_es || '',
      italien: lookbook.description_it || '',
    },
    collection: lookbook.id_collection || '',
    annee: lookbook.annee || '',
    couleurCadre: data.couleur_cadre || 'FFFFFF',
    couleurTexte: data.couleur_texte || '000000',
    banniere: banniereUrl,
    categories: [], // Sera rempli séparément
    actif: lookbook.actif,
    dateCreation: lookbook.date_creation,
  }
}

/**
 * Transforme les données du formulaire en input pour le backend
 */
export function transformFormDataToInput(
  formData: LookbookFormData,
  idVendeur: number,
): CreateLookbookInput {
  return {
    id_vendeur: idVendeur,
    type: 1, // Type par défaut
    annee: formData.annee,
    id_collection: formData.collection, // "printemps-ete" ou "automne-hiver"
    data: JSON.stringify({
      couleur_cadre: formData.couleurCadre,
      couleur_texte: formData.couleurTexte,
    }),
    titre_fr: formData.titre.francais,
    titre_en: formData.titre.anglais,
    titre_es: formData.titre.espagnol,
    titre_it: formData.titre.italien,
    description_fr: formData.description.francais,
    description_en: formData.description.anglais,
    description_es: formData.description.espagnol,
    description_it: formData.description.italien,
    actif: false,
  }
}

// ============= API LOOKBOOKS =============

/**
 * Récupérer tous les lookbooks
 */
export async function getAllLookbooks(): Promise<LookbookDisplay[]> {
  const query = `
    query {
      lookbooks {
        id_mod_lookbook
        id_vendeur
        type
        annee
        id_collection
        data
        titre_fr
        titre_en
        titre_es
        titre_it
        description_fr
        description_en
        description_es
        description_it
        actif
        date_creation
        supprimer
        banniere_url
      }
    }
  `

  const response = await fetchApi<{ lookbooks: any[] }>(query)
  return response.lookbooks.map(transformLookbookToDisplay)
}

/**
 * Récupérer les lookbooks d'un vendeur
 */
export async function getLookbooksByVendeur(
  idVendeur: number | string,
): Promise<LookbookDisplay[]> {
  const query = `
    query GetLookbooksByVendeur($idVendeur: Int!) {
      lookbooksByVendeur(id_vendeur: $idVendeur) {
        id_mod_lookbook
        id_vendeur
        type
        annee
        id_collection
        data
        titre_fr
        titre_en
        titre_es
        titre_it
        description_fr
        description_en
        description_es
        description_it
        actif
        date_creation
        supprimer
        banniere_url
      }
    }
  `

  const response = await fetchApi<{ lookbooksByVendeur: any[] }>(query, {
    idVendeur: typeof idVendeur === 'string' ? parseInt(idVendeur) : idVendeur,
  })
  return response.lookbooksByVendeur.map(transformLookbookToDisplay)
}

/**
 * Récupérer un lookbook par ID
 */
export async function getLookbookById(id: number | string): Promise<LookbookDisplay> {
  const query = `
    query GetLookbook($id: Int!) {
      lookbook(id: $id) {
        id_mod_lookbook
        id_vendeur
        type
        annee
        id_collection
        data
        titre_fr
        titre_en
        titre_es
        titre_it
        description_fr
        description_en
        description_es
        description_it
        actif
        date_creation
        supprimer
        banniere_url
      }
    }
  `

  const response = await fetchApi<{ lookbook: any }>(query, { 
    id: typeof id === 'string' ? parseInt(id) : id 
  })
  return transformLookbookToDisplay(response.lookbook)
}

/**
 * Créer un nouveau lookbook
 */
export async function createLookbook(
  input: CreateLookbookInput,
): Promise<LookbookDisplay> {
  const query = `
    mutation CreateLookbook($input: CreateLookbookInput!) {
      createLookbook(input: $input) {
        id_mod_lookbook
        id_vendeur
        type
        annee
        id_collection
        data
        titre_fr
        titre_en
        titre_es
        titre_it
        description_fr
        description_en
        description_es
        description_it
        actif
        date_creation
        supprimer
        banniere_url
      }
    }
  `

  const response = await fetchApi<{ createLookbook: any }>(query, {
    input,
  })
  return transformLookbookToDisplay(response.createLookbook)
}

/**
 * Mettre à jour un lookbook
 */
export async function updateLookbook(
  input: UpdateLookbookInput,
): Promise<LookbookDisplay> {
  const query = `
    mutation UpdateLookbook($input: UpdateLookbookInput!) {
      updateLookbook(input: $input) {
        id_mod_lookbook
        id_vendeur
        type
        annee
        id_collection
        data
        titre_fr
        titre_en
        titre_es
        titre_it
        description_fr
        description_en
        description_es
        description_it
        actif
        date_creation
        supprimer
        banniere_url
      }
    }
  `

  const response = await fetchApi<{ updateLookbook: any }>(query, {
    input,
  })
  return transformLookbookToDisplay(response.updateLookbook)
}

/**
 * Supprimer un lookbook (soft delete)
 */
export async function deleteLookbook(id: number | string): Promise<boolean> {
  const query = `
    mutation RemoveLookbook($id: Int!) {
      removeLookbook(id: $id)
    }
  `

  const response = await fetchApi<{ removeLookbook: boolean }>(query, { 
    id: typeof id === 'string' ? parseInt(id) : id 
  })
  return response.removeLookbook
}

/**
 * Activer/Désactiver un lookbook
 */
export async function toggleLookbookActif(
  id: number | string,
): Promise<LookbookDisplay> {
  const query = `
    mutation ToggleLookbookActif($id: Int!) {
      toggleLookbookActif(id: $id) {
        id_mod_lookbook
        id_vendeur
        type
        annee
        id_collection
        data
        titre_fr
        titre_en
        titre_es
        titre_it
        description_fr
        description_en
        description_es
        description_it
        actif
        date_creation
        supprimer
        banniere_url
      }
    }
  `

  const response = await fetchApi<{ toggleLookbookActif: any }>(query, {
    id: typeof id === 'string' ? parseInt(id) : id,
  })
  return transformLookbookToDisplay(response.toggleLookbookActif)
}

// ============= API FICHES =============

/**
 * Créer une nouvelle fiche
 */
export async function createLookbookFiche(
  idModLookbook: number | string,
  couverture = false,
): Promise<LookbookFiche> {
  const query = `
    mutation CreateLookbookFiche($input: CreateLookbookFicheInput!) {
      createLookbookFiche(input: $input) {
        id_mod_lookbook_fiche
        id_mod_lookbook
        couverture
        date_creation
        ordre
        supprimer
      }
    }
  `

  const response = await fetchApi<{ createLookbookFiche: LookbookFiche }>(
    query,
    {
      input: {
        id_mod_lookbook: typeof idModLookbook === 'string' ? parseInt(idModLookbook) : idModLookbook,
        // Backend expects an Int (0/1), not a boolean
        couverture: couverture ? 1 : 0,
      },
    },
  )
  return response.createLookbookFiche
}

/**
 * Récupérer les fiches d'un lookbook
 */
export async function getLookbookFiches(
  idModLookbook: number | string,
): Promise<LookbookFiche[]> {
  const query = `
    query GetLookbookFichesByLookbook($idModLookbook: Int!) {
      lookbookFichesByLookbook(id_mod_lookbook: $idModLookbook) {
        id_mod_lookbook_fiche
        id_mod_lookbook
        couverture
        date_creation
        ordre
        supprimer
        description_fr
        description_en
        description_es
        description_it
        image_url
      }
    }
  `

  const response = await fetchApi<{
    lookbookFichesByLookbook: LookbookFiche[]
  }>(query, { 
    idModLookbook: typeof idModLookbook === 'string' ? parseInt(idModLookbook) : idModLookbook 
  })
  return response.lookbookFichesByLookbook
}

/**
 * Définir une fiche comme couverture
 */
export async function setFicheCouverture(
  idFiche: number,
): Promise<LookbookFiche> {
  const query = `
    mutation SetLookbookFicheCouverture($id: Int!) {
      setLookbookFicheCouverture(id: $id) {
        id_mod_lookbook_fiche
        id_mod_lookbook
        couverture
        date_creation
        ordre
        supprimer
      }
    }
  `

  const response = await fetchApi<{
    setLookbookFicheCouverture: LookbookFiche
  }>(query, { id: typeof idFiche === 'string' ? parseInt(idFiche) : idFiche })
  return response.setLookbookFicheCouverture
}

/**
 * Changer l'ordre d'une fiche
 */
export async function changeFicheOrdre(
  idFiche: number,
  direction: 'prev' | 'next',
): Promise<LookbookFiche> {
  const query = `
    mutation ChangeLookbookFicheOrdre($id: Int!, $direction: String!) {
      changeLookbookFicheOrdre(id: $id, direction: $direction) {
        id_mod_lookbook_fiche
        id_mod_lookbook
        couverture
        date_creation
        ordre
        supprimer
      }
    }
  `

  const response = await fetchApi<{ changeLookbookFicheOrdre: LookbookFiche }>(
    query,
    { id: typeof idFiche === 'string' ? parseInt(idFiche) : idFiche, direction },
  )
  return response.changeLookbookFicheOrdre
}

/**
 * Supprimer une fiche
 */
export async function deleteLookbookFiche(idFiche: number): Promise<boolean> {
  const query = `
    mutation RemoveLookbookFiche($id: Int!) {
      removeLookbookFiche(id: $id)
    }
  `

  const response = await fetchApi<{ removeLookbookFiche: boolean }>(query, {
    id: typeof idFiche === 'string' ? parseInt(idFiche) : idFiche,
  })
  return response.removeLookbookFiche
}

// ============= API PRODUITS =============

/**
 * Mettre à jour une fiche de lookbook
 */
export async function updateLookbookFiche(
  idFiche: number | string,
  descriptions: {
    description_fr?: string
    description_en?: string
    description_es?: string
    description_it?: string
  },
): Promise<LookbookFiche> {
  const query = `
    mutation UpdateLookbookFiche($input: UpdateLookbookFicheInput!) {
      updateLookbookFiche(input: $input) {
        id_mod_lookbook_fiche
        id_mod_lookbook
        couverture
        date_creation
        ordre
        supprimer
        description_fr
        description_en
        description_es
        description_it
        image_url
      }
    }
  `

  const response = await fetchApi<{
    updateLookbookFiche: LookbookFiche
  }>(query, {
    input: {
      id_mod_lookbook_fiche: typeof idFiche === 'string' ? parseInt(idFiche) : idFiche,
      ...descriptions,
    },
  })
  return response.updateLookbookFiche
}

/**
 * Ajouter un produit à une fiche
 */
export async function addProduitToFiche(
  idFiche: number | string,
  idProduit: number | string,
): Promise<LookbookFicheProduit> {
  const query = `
    mutation CreateLookbookFicheProduit($input: CreateLookbookFicheProduitInput!) {
      createLookbookFicheProduit(input: $input) {
        id_mod_lookbook_fiche_produit
        id_mod_lookbook_fiche
        id_produit
      }
    }
  `

  const response = await fetchApi<{
    createLookbookFicheProduit: LookbookFicheProduit
  }>(query, {
    input: {
      id_mod_lookbook_fiche: typeof idFiche === 'string' ? parseInt(idFiche) : idFiche,
      id_produit: typeof idProduit === 'string' ? parseInt(idProduit) : idProduit,
    },
  })
  return response.createLookbookFicheProduit
}

/**
 * Récupérer les produits d'une fiche
 */
export async function getFicheProduits(
  idFiche: number | string,
): Promise<LookbookFicheProduit[]> {
  const query = `
    query GetLookbookFicheProduitsByFiche($idFiche: Int!) {
      lookbookFicheProduitsByFiche(id_mod_lookbook_fiche: $idFiche) {
        id_mod_lookbook_fiche_produit
        id_mod_lookbook_fiche
        id_produit
      }
    }
  `

  const response = await fetchApi<{
    lookbookFicheProduitsByFiche: LookbookFicheProduit[]
  }>(query, { 
    idFiche: typeof idFiche === 'string' ? parseInt(idFiche) : idFiche 
  })
  return response.lookbookFicheProduitsByFiche
}

/**
 * Supprimer un produit d'une fiche
 */
export async function removeProduitFromFiche(
  idFiche: number | string,
  idProduit: number | string,
): Promise<boolean> {
  const query = `
    mutation RemoveLookbookFicheProduitByIds($idFiche: Int!, $idProduit: Int!) {
      removeLookbookFicheProduitByIds(id_mod_lookbook_fiche: $idFiche, id_produit: $idProduit)
    }
  `

  const response = await fetchApi<{
    removeLookbookFicheProduitByIds: boolean
  }>(query, { 
    idFiche: typeof idFiche === 'string' ? parseInt(idFiche) : idFiche,
    idProduit: typeof idProduit === 'string' ? parseInt(idProduit) : idProduit
  })
  return response.removeLookbookFicheProduitByIds
}

// ============= API CATEGORIES =============

/**
 * Récupérer les catégories d'un lookbook
 */
export async function getLookbookCategories(
  idLookbook: number | string,
): Promise<LookbookCategorie[]> {
  const query = `
    query GetLookbookCategoriesByLookbook($idLookbook: Int!) {
      lookbookCategoriesByLookbook(id_mod_lookbook: $idLookbook) {
        id_mod_lookbook_categorie
        id_mod_lookbook
        id_categorie
      }
    }
  `

  const response = await fetchApi<{
    lookbookCategoriesByLookbook: LookbookCategorie[]
  }>(query, { 
    idLookbook: typeof idLookbook === 'string' ? parseInt(idLookbook) : idLookbook 
  })
  return response.lookbookCategoriesByLookbook
}

/**
 * Sauvegarder les catégories d'un lookbook
 */
export async function saveLookbookCategories(
  idLookbook: number | string,
  categories: number[],
): Promise<LookbookCategorie[]> {
  const query = `
    mutation SaveLookbookCategories($idLookbook: Int!, $categories: [Int!]!) {
      saveLookbookCategories(id_mod_lookbook: $idLookbook, categories: $categories) {
        id_mod_lookbook_categorie
        id_mod_lookbook
        id_categorie
      }
    }
  `

  const response = await fetchApi<{
    saveLookbookCategories: LookbookCategorie[]
  }>(query, { 
    idLookbook: typeof idLookbook === 'string' ? parseInt(idLookbook) : idLookbook, 
    categories 
  })
  return response.saveLookbookCategories
}

// ============= UPLOAD IMAGES =============

/**
 * Uploader la bannière d'un lookbook
 */
export async function uploadLookbookBanniere(
  idLookbook: number | string,
  file: File,
): Promise<{ success: boolean; message: string; url?: string }> {
  const formData = new FormData()
  formData.append('image', file)
  formData.append('idLookbook', typeof idLookbook === 'string' ? idLookbook : idLookbook.toString())

    const apiUrl = process.env.VUE_APP_API_BASE_URL
  const response = await fetchRest(`${apiUrl}/api/upload-lookbook-banniere`, {
    method: 'POST',
    body: formData,
  })

  return response.json()
}

// Upload d'image pour une fiche
export async function uploadLookbookFiche(
  idLookbook: number | string,
  idFiche: number | string,
  file: File,
): Promise<{ success: boolean; message: string; url?: string }> {
  const formData = new FormData()
  formData.append('image', file)
  formData.append('idLookbook', idLookbook.toString())
  formData.append('idFiche', idFiche.toString())

    const VUE_APP_API_BASE_URL = process.env.VUE_APP_API_BASE_URL
  const response = await fetchRest(`${VUE_APP_API_BASE_URL}/api/upload-lookbook-fiche`, {
    method: 'POST',
    body: formData,
  })

  return response.json()
}

